import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SwitchWebhookService } from 'src/app/_services/switch-webhook.service';
import {
  FormGroup,
  FormArray,
  FormControl,
  FormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { UserService } from 'src/app/_services';
import { MessageService } from 'primeng/api';
import { HttpEventType } from '@angular/common/http';

export function noSpecialCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || typeof control.value !== 'string') {
      return null;
    }
    const regex = /[!@#$%^&*(),.?":{}|<>]/;
    return regex.test(control.value) ? { specialChars: true } : null;
  };
}

@Component({
  selector: 'app-switch-webhook-dialog',
  templateUrl: './switch-webhook-dialog.component.html',
  styleUrls: ['./switch-webhook-dialog.component.scss'],
})
export class SwitchWebhookDialogComponent implements OnInit {
  data: any;
  dropdownFields: any[] = [];
  textFields: any[] = [];
  numberFields: any[] = [];
  dateFields: any[] = [];
  emailFields: any[] = [];
  fileFields: any[] = [];
  loading: boolean = false;
  selectedFile: any;
  uploadedFile: number = 0;
  form: FormGroup;
  userId: any;
  allFields: any[] = [];
  files: any[] = [];
  uploadProgress: number | null = null;

  constructor(
    public ref: DynamicDialogRef,
    private switchWebhookService: SwitchWebhookService,
    public config: DynamicDialogConfig,
    private userService: UserService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.form = this.fb.group({
      fields: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.data = this.config.data;

    this.userService.currentUser$.subscribe((user) => {
      this.userId = user.id;
    });
    this.switchWebhookService
      .getWebhookFields(this.data.id)
      .subscribe((data: any) => {
        this.allFields = data.body;
        console.log('All Fields:', this.allFields);

        this.populateFormFields();
      });
  }

  populateFormFields() {
    const formGroups = this.allFields.map((field: any) => {
      const validators: ValidatorFn[] = [];

      // Required logic
      if (field.field_required) {
        if (field.field_name === 'file') {
          validators.push((control) => {
            const hasFiles = this.files && this.files.length > 0;
            return hasFiles ? null : { required: true };
          });
        } else {
          validators.push(Validators.required);
        }
      }

      // STRICT email validation
      if (field.field_name === 'email') {
        validators.push(Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/));
      }

      return this.fb.group({
        field_id: [field.id],
        field_name: [field.field_name],
        field_title: [field.field_title],
        field_title_value: [field.field_title_value],
        value: this.fb.control('', {
          validators,
          updateOn: 'blur',
        }),
      });
    });

    this.form = this.fb.group({
      fields: this.fb.array(formGroups),
    });
  }

  get fields(): FormArray {
    return this.form.get('fields') as FormArray;
  }

  onFileSelect(event: any, index: number) {
    let selectedFiles: File[] = [];

    if (event.currentFiles && Array.isArray(event.currentFiles)) {
      // newer PrimeNG versions
      selectedFiles = event.currentFiles;
    }

    // Store files for this specific field index
    const field = this.allFields[index];
    if (field && field.field_name === 'file') {
      // Clear previous files for this field and add new ones
      this.files = this.files.filter((f, idx) => {
        // Keep files that don't belong to this field
        return idx !== index;
      });

      selectedFiles.forEach((file) => {
        this.files.push(file);
      });

      // Update the form control value to trigger validation
      const fieldControl = this.fields.at(index).get('value');
      if (fieldControl) {
        if (selectedFiles.length > 0) {
          fieldControl.setValue('files_selected');
        } else {
          fieldControl.setValue('');
        }
        fieldControl.updateValueAndValidity();
        fieldControl.markAsTouched();
      }
    }
  }

  // Form submission handler
  submitForm() {
    console.log('Form Values:', this.form.value);
    
    // Mark all fields as touched to show validation errors
    this.form.markAllAsTouched();
    this.fields.controls.forEach((control) => {
      control.markAllAsTouched();
    });

    // Re-validate file fields
    this.allFields.forEach((field, index) => {
      if (field.field_name === 'file' && field.field_required) {
        const fieldControl = this.fields.at(index).get('value');
        if (fieldControl) {
          fieldControl.updateValueAndValidity();
        }
      }
    });

    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        key: 'switch-webhook-error',
      });
      return;
    }

    this.loading = true;

    const formValues = this.form.value;
    const consolidatedData: { [key: string]: any } = {};

    formValues.fields.forEach((field: any) => {
      const uniqueKey = field.field_title_value;
      consolidatedData[uniqueKey] = field.value ?? "";
    });
    
    const submissionData = {
      webhook_id: this.data.id,
      user_id: this.userId,
      webhook_url: this.data.webhook_url,
      webhook_name: this.data.webhook_name,
      submission_data: [consolidatedData],
    };

    const formData = new FormData();

    // 🔑 REQUIRED BY FASTAPI
    formData.append('submission_data', JSON.stringify(submissionData));

    // 🔑 EACH FILE IS APPENDED SEPARATELY
    this.files.forEach((file) => {
      formData.append('files', file);
    });

    console.log('FormData prepared for submission:', submissionData);
    

    this.uploadProgress = 0;

    const totalFileSize = this.files.reduce((total, file) => total + file.size, 0);
    if(totalFileSize > 250 * 1024 * 1024) {
      this.loading = false;
      this.uploadProgress = null;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Upload failed: total file size exceeds 250MB',
        key: 'switch-webhook-error',
      });
      return;
    }

    this.switchWebhookService
      .postWebhookSubmission(this.data.id, this.userId, formData)
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round(
              (event.loaded / event.total) * 100
            );
          }

          if (event.type === HttpEventType.Response) {
            this.uploadProgress = 100;
            this.loading = false;

            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Submission created successfully',
              key: 'switch-webhook-success',
            });

            this.form.reset();
            this.files = [];
            this.ref.close();
          }
        },
        error: (err) => {
          this.loading = false;
          this.uploadProgress = null;
          console.error("THIS IS AN ERROR", err);

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Submission failed - ${err}`,
            key: 'switch-webhook-error',
          });
        },
      });
  }
}
